import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Quote } from "lucide-react";

export default function CaseStudy() {
  return (
    <section
      id="case-study"
      data-testid="case-study-section"
      className="relative py-24 md:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="mb-12 flex items-end justify-between flex-wrap gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-4">
              Proof · BFSI Lending
            </p>
            <h2 className="font-editorial font-light text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter max-w-3xl">
              From <span className="italic">1–2%</span> to a pipeline
              <br />
              that <span className="italic">pays.</span>
            </h2>
          </div>
          <a
            href="https://vridhi-home-finance-crm.vercel.app/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="case-study-demo-link"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.25em] text-[#EDEDED] border-b border-[#FF4500] pb-1 hover:text-[#FF4500] transition-colors"
          >
            Demo CRM Dashboard <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="grid lg:grid-cols-2 gap-6"
        >
          {/* Left: narrative */}
          <div className="rounded-3xl border border-[#2A2E3D] bg-[#151821] p-8 md:p-12 relative overflow-hidden flex flex-col justify-between">
            <Quote className="absolute -top-4 -right-4 w-40 h-40 text-[#FF4500]/5" />
            <div className="relative space-y-10">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF4500] mb-3">
                  The problem
                </p>
                <p className="text-[#EDEDED] text-xl sm:text-2xl font-editorial leading-snug tracking-tight">
                  An NBFC ran loan campaigns through ads. Thousands of leads in,
                  only <span className="text-[#FF4500]">1–2%</span>{" "}
                  ever converted. No tracking, no qualification, no next step.
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#A7F3D0] mb-3">
                  What we did
                </p>
                <p className="text-[#EDEDED] text-xl sm:text-2xl font-editorial leading-snug tracking-tight">
                  Layered{" "}
                  <span className="italic text-[#EDEDED]">augur.bot</span>{" "}
                  to track every lead by stage, then{" "}
                  <span className="italic text-[#EDEDED]">forsyt.com</span>{" "}
                  to auto-trigger the next-best action, fixing the leak at the
                  core.
                </p>
              </div>
            </div>
          </div>

          {/* Right: massive stat block */}
          <div className="rounded-3xl border border-[#2A2E3D] bg-gradient-to-br from-[#FF4500]/15 via-[#151821] to-[#0F1117] p-8 md:p-12 flex flex-col justify-between min-h-[360px] relative overflow-hidden">
            <div className="absolute -bottom-20 -right-10 w-72 h-72 rounded-full bg-[#FF4500]/20 blur-[100px]" />
            <div className="relative">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#EDEDED]/80 mb-6">
                Outcome
              </p>
              <div className="tick-num text-[8rem] sm:text-[10rem] text-[#FF4500] leading-[0.8]">
                +25%
              </div>
              <p className="mt-6 font-editorial italic text-2xl text-[#EDEDED] max-w-xs">
                lift in conversions on the same lead flow.
              </p>
            </div>
            <a
              href="https://vridhi-home-finance-crm.vercel.app/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="case-study-cta"
              className="relative mt-8 inline-flex items-center gap-2 self-start bg-[#0B0D12]/70 border border-[#2A2E3D] hover:border-[#FF4500] text-[#EDEDED] px-5 py-3 rounded-full text-sm transition-all"
            >
              Open live CRM dashboard <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </motion.div>

        {/* Testimonial */}
        <motion.blockquote
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          data-testid="testimonial"
          className="mt-16 max-w-4xl"
        >
          <p className="font-editorial italic text-3xl sm:text-4xl lg:text-5xl text-[#EDEDED] leading-tight tracking-tight">
            “We stopped chasing thousands of dead leads.
            <br />
            Prospectivly handed our team meetings that actually closed.”
          </p>
          <footer className="mt-6 text-sm uppercase tracking-[0.25em] text-[#9CA3AF]">
            Founder, augur.bot
          </footer>
        </motion.blockquote>
      </div>
    </section>
  );
}
